import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createFileLibraryDocumentComment,
  listFileLibraryDocumentComments,
} from "@/api/file_library_document_comments";
import { CommentThread } from "@/components/comments/CommentThread";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

type DocumentCommentsPanelProps = {
  municipalityId: number;
  documentId: number;
  className?: string;
  /** Align header chrome with the draft editor toolbar when embedded in a split panel. */
  embedded?: boolean;
  /** When set, refreshes grant opportunity activity after posting a comment. */
  opportunityId?: number;
};

export function DocumentCommentsPanel({
  municipalityId,
  documentId,
  className,
  embedded = false,
  opportunityId,
}: DocumentCommentsPanelProps) {
  const queryClient = useQueryClient();
  const queryKey = ["file_library_document_comments", municipalityId, documentId] as const;

  const { data: comments = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listFileLibraryDocumentComments(municipalityId, documentId),
  });

  const createMutation = useMutation({
    mutationFn: ({ body, parentId }: { body: string; parentId?: number | null }) =>
      createFileLibraryDocumentComment(municipalityId, documentId, body, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ["grant_opportunity_linked_document_comments", municipalityId],
      });
      if (opportunityId != null) {
        queryClient.invalidateQueries({
          queryKey: [
            "grant_opportunity_linked_document_comments",
            municipalityId,
            opportunityId,
          ],
        });
      }
    },
  });

  return (
    <div
      className={cn("flex h-full min-h-0 w-full flex-col bg-background", className)}
      data-testid="file-center-document-comments"
    >
      <div
        className={cn(
          "shrink-0 border-b",
          embedded ? "bg-muted/20 px-3 py-2" : "flex h-11 items-center px-3",
        )}
      >
        <h2 className="text-sm font-semibold">Team comments</h2>
      </div>
      <ScrollArea className="min-h-0 flex-1" scrollbarSize="lg">
        <div className="p-3">
          {!embedded ? (
            <p className="mb-3 text-sm text-muted-foreground">
              Notes on this file are visible to everyone on your municipality team.
            </p>
          ) : null}
          <CommentThread
            comments={comments}
            isLoading={isLoading}
            error={Boolean(error)}
            submitting={createMutation.isPending}
            onCreateComment={async (body, parentId) => {
              await createMutation.mutateAsync({ body, parentId });
            }}
            testId="document-comments-thread"
            variant="sidebar"
          />
        </div>
      </ScrollArea>
    </div>
  );
}
